// src/components/Auth/LoginModal.tsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, Mail, Globe, Apple } from 'lucide-react';
import { API_BASE_URL, AUTH_SETTINGS, FEATURES } from '../../config';

interface LoginModalProps {
  sessionToken?: string;
  userEmail?: string;
  onBack?: () => void;
  onLoginSuccess?: (provider?: string) => void;
  onSignUp?: () => void; // New prop for navigation to signup
}

export function LoginModal({ sessionToken, userEmail, onBack, onLoginSuccess, onSignUp }: LoginModalProps) {
  const [email, setEmail] = useState(userEmail || '');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [socialLoginPending, setSocialLoginPending] = useState<string | null>(null);
  
  // Load remembered email if available
  useEffect(() => {
    if (!userEmail) {
      const rememberedEmail = localStorage.getItem(AUTH_SETTINGS.REMEMBER_EMAIL_KEY);
      if (rememberedEmail) {
        setEmail(rememberedEmail);
        setRememberMe(true);
      }
    }
    
    // Check for error in localStorage that might have been set during login
    const loginFailed = localStorage.getItem('tripperLoginFailed');
    if (loginFailed === 'true') {
      setErrorMessage('Invalid Email or Password');
      localStorage.removeItem('tripperLoginFailed');
    }
  }, [userEmail]);

  // Password field keydown handler for Enter key
  const handlePasswordKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      e.preventDefault();
      handleDirectLogin();
    }
  };

  // DIRECT LOGIN FUNCTION - No service calls, just fetch
  const handleDirectLogin = async () => {
    // Prevent processing during loading
    if (isLoading) return;
    
    // Reset error
    setErrorMessage('');
    
    // Basic validation
    if (!email.trim()) {
      setErrorMessage('Please enter your email');
      return;
    }
    if (!password.trim()) {
      setErrorMessage('Please enter your password');
      return;
    }
    
    // Handle "remember me" option
    if (rememberMe) {
      localStorage.setItem(AUTH_SETTINGS.REMEMBER_EMAIL_KEY, email);
    } else {
      localStorage.removeItem(AUTH_SETTINGS.REMEMBER_EMAIL_KEY);
    }
    
    // Start loading
    setIsLoading(true);
    
    try {
      // Browser info for session logging
      const browserInfo = {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
      };
      
      // Prepare login data
      const loginData = { 
        email, 
        password,
        sessionData: {
          browserInfo,
          timestamp: Date.now(),
        }
      };
      
      // Include session token if provided
      if (sessionToken) {
        Object.assign(loginData, { sessionToken });
      }
      
      console.log('Making direct login request to:', `${API_BASE_URL}/api/auth/login`);
      
      // Make the fetch call directly
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginData)
      });

      // Parse the response
      const result = await response.json();
      
      // Check if successful
      if (response.ok && result.success) {
        console.log('Login successful:', result);
        
        // Store token and user data
        localStorage.setItem('authToken', result.token);
        localStorage.setItem('tripperAuthenticated', 'true');
        localStorage.setItem('tripperHasAccount', 'true');
        localStorage.setItem('tripperUserEmail', email);
        
        if (result.user) {
          localStorage.setItem('tripperUserName', result.user.name || '');
          localStorage.setItem('tripperUserId', result.user.id);
          localStorage.setItem('userData', JSON.stringify(result.user));
        }
        
        // Remove any logout flag
        localStorage.removeItem('tripperLoggedOut');
        
        // Notify of success
        if (onLoginSuccess) {
          onLoginSuccess();
        }
        
        // Dispatch authenticated event
        window.dispatchEvent(new Event('tripperAuthenticated'));
      } else {
        // Handle error
        console.error('Login failed:', result.message || 'Unknown error');
        setErrorMessage(result.message || 'Invalid Email or Password');
        
        // Set login failed flag in case we need it later
        localStorage.setItem('tripperLoginFailed', 'true');
      }
    } catch (error) {
      console.error('Login request error:', error);
      setErrorMessage('Network error. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle social login - Direct implementation
  const handleProviderLogin = (provider: 'google' | 'microsoft' | 'apple') => {
    setSocialLoginPending(provider);
    setErrorMessage('');
    
    // Store current email to help with account linking
    if (email) {
      sessionStorage.setItem('lastAttemptedEmail', email);
    }
    
    // Also store any code validation state to preserve it across redirect
    if (localStorage.getItem('tripperCodeValidated') === 'true') {
      localStorage.setItem('tripperCodeValidatedBeforeSocial', 'true');
    }
    
    // Store provider in session storage for after redirect
    sessionStorage.setItem('socialAuthProvider', provider);
    
    // Redirect to the provider's auth page
    window.location.href = `${API_BASE_URL}/api/auth/${provider}`;
  };

  return (
    <>
      {/* Header */}
      <div className="px-6 flex flex-col justify-center border-b border-gray-200" style={{ 
        height: 'var(--auth-panel-header-height)' 
      }}>
        <h2 className="text-gray-900" style={{
          fontSize: 'var(--auth-font-size-2xl)',
          fontWeight: 'var(--auth-font-weight-regular)',
          fontFamily: 'var(--auth-header-font-family)'
        }}>Sign In to Tripper</h2>
      </div>
      {/* <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Sign in to Tripper</h2>
        <p className="text-sm text-gray-500 mt-1">
          Login or create an account to continue
        </p>
      </div> */}

      <div className="p-6 space-y-6">
        {/* Social Login Buttons - with better spacing and Apple removed */}
        {FEATURES.SOCIAL_LOGIN && (
          <div className="space-y-4">
            <button
                type="button"
                onClick={() => handleProviderLogin('google')}
                className="w-full border border-gray-300 rounded-md flex items-center justify-center gap-3 hover:bg-gray-50 transition-colors relative"
                style={{
                  height: 'var(--auth-button-height)',
                  padding: 'var(--auth-button-padding)',
                  fontSize: 'var(--auth-button-font-size)',
                  fontWeight: 'var(--auth-button-font-weight)',
                  borderRadius: 'var(--auth-button-border-radius)'
                }}
              >
              <svg className="w-5 h-5" style={{ 
                  width: 'var(--auth-button-icon-size)', 
                  height: 'var(--auth-button-icon-size)' 
                }} viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span>Sign in with Google</span>
              {socialLoginPending === 'google' && (
                <span className="absolute right-3 h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></span>
              )}
            </button>

            <button
              type="button"
              onClick={() => handleProviderLogin('microsoft')}
              disabled={isLoading || socialLoginPending !== null}
              className="w-full border border-gray-300 rounded-md flex items-center justify-center gap-3 hover:bg-gray-50 transition-colors relative"
                style={{
                  height: 'var(--auth-button-height)',
                  padding: 'var(--auth-button-padding)',
                  fontSize: 'var(--auth-button-font-size)',
                  fontWeight: 'var(--auth-button-font-weight)',
                  borderRadius: 'var(--auth-button-border-radius)'
                }}
              >
              <Globe size={20} className="text-blue-500" />
              
              <span>Sign in with Microsoft</span>
              {socialLoginPending === 'microsoft' && (
                <span className="absolute right-3 h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></span>
              )}
            </button>
          </div>
        )}

        <div className="relative py-2">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white text-gray-500">Or</span>
          </div>
        </div>
        {/* Email Login Section with improved spacing */}
        <div className="space-y-6"> {/* Increased from space-y-5 */}
          <div>
            <label 
              htmlFor="email" 
              className="block text-gray-700 mb-3"
              style={{
                fontSize: 'var(--auth-label-font-size)',
                fontWeight: 'var(--auth-label-font-weight)',
                fontFamily: 'var(--auth-font-family)'
              }}
            >
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              style={{
                borderRadius: 'var(--auth-button-border-radius)'
              }}
              placeholder="Enter your email address"
              disabled={isLoading || !!userEmail}
            />
          </div>

          <div className="mt-6"> {/* Added explicit top margin */}
            <div className="flex justify-between mb-3"> {/* Increased from mb-2 */}
              <label 
                htmlFor="password" 
                className="block text-gray-700"
                style={{
                  fontSize: 'var(--auth-label-font-size)',
                  fontWeight: 'var(--auth-label-font-weight)',
                  fontFamily: 'var(--auth-font-family)'
                }}
              >
                Password
              </label>
            </div>
            <input
              type="password"
              id="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={handlePasswordKeyDown}
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              style={{
                borderRadius: 'var(--auth-button-border-radius)'
              }}
              placeholder="Enter your password"
              disabled={isLoading}
            />
              <a 
                href="#" 
                className="text-sm text-blue-600 hover:underline block text-right"
                style={{ padding: '1rem 1rem' }}
              >
                Forgot password?
              </a>
          </div>

          {/* <div className="flex items-center mt-5"> 
            <input
              id="remember-me"
              name="remember-me"
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              disabled={isLoading}
            />
            <label htmlFor="remember-me" className="ml-3 block text-sm text-gray-700">
              Remember me
            </label>
          </div> */}

          {/* Error Message */}
          <AnimatePresence>
            {errorMessage && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="flex items-start gap-2 text-red-600 text-sm p-3 bg-red-50 border border-red-100 rounded-md mt-4" /* Added mt-4 */
              >
                <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                <span>{errorMessage}</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Login Button - with better spacing */}
          <button
            type="button"
            onClick={handleDirectLogin}
            disabled={isLoading}
            className={`w-full rounded-md text-white font-medium flex items-center justify-center gap-2 mt-6
              ${isLoading 
                ? 'bg-blue-400 cursor-not-allowed' 
                : 'bg-blue-600 hover:bg-blue-700 transition-colors'
              }`}
            style={{
              height: 'var(--auth-button-height)',
              padding: 'var(--auth-button-padding)',
              fontSize: 'var(--auth-button-font-size)',
              fontWeight: 'var(--auth-button-font-weight)',
               borderRadius: 'var(--auth-button-border-radius)'
            }}
          > 
            {isLoading ? (
              <>
                <span className="h-5 w-5 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                <span>Signing in...</span>
              </>
            ) : (
              <>
                <Mail size={20} />
                <span>Sign In</span>
              </>
            )}
          </button>
        </div>

        <div className="text-sm text-gray-500 text-center space-y-3 mt-4">
          <p>
            Don't have an account? {/* Replace href with onClick handler */}
            <button
              type="button"
              onClick={onSignUp}
              className="text-blue-600 hover:underline bg-transparent border-none p-0 m-0 inline font-normal"
            >
              Sign up
            </button>
          </p>
          <p className="text-xs">
            By continuing, you agree to our <a href="/terms-of-use.html" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Terms of Use</a> and <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Privacy Policy</a>.
          </p>
        </div>
        {/* Back Button (if in invite flow) */}
        {/* {onBack && (
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={onBack}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              ← Back to invite code
            </button>
          </div>
        )} */}
      </div>
    </>
  );
}